import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import DataTable from 'TailwindcssComponents/Tables';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import { Icon } from "@iconify/react";
import FooterTailwind from 'TailwindcssComponents/Footer';
import { CSVLink } from "react-csv";

export default function DetailedTicketPageByEvents() {
    const { id } = useParams();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(true);
    const [isEventLoading, setIsEventLoading] = useState(false);
    const [eventsData, setEventsData] = useState([]);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [ticketsData, setTicketsData] = useState([]);
    const [ticketsTableData, setTicketsTableData] = useState([]);
    const [registrationsData, setRegistrationsData] = useState([]);
    const [totalTicketsCount, setTotalTicketsCount] = useState(0);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
    const navigate = useNavigate();


    const fetchEvent = async () => {
        try {
            setIsEventLoading(true);
            // Fetch all required data in parallel
            const [eventResponse, ticketResponse, registrationsResponse, paymentResponse, categoryResponse] = await Promise.all([
                axios.get(`${apiUrl}event-service/event/${id}`),
                axios.get(`${apiUrl}event-service/event-ticket`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/event-registration`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/event-payment`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/ticket-category`, { params: { event: id } }),
            ]);

            // Validate responses
            if (!eventResponse.data?.success || !eventResponse.data?.data) throw new Error('Failed to fetch event');
            if (!ticketResponse.data?.success || !ticketResponse.data?.data) throw new Error('Failed to fetch tickets');
            if (!registrationsResponse.data?.success || !registrationsResponse.data?.data) throw new Error('Failed to fetch registrations');
            if (!paymentResponse.data?.success || !paymentResponse.data?.data) throw new Error('Failed to fetch payments');
            if (!categoryResponse.data?.success || !categoryResponse.data?.data) throw new Error('Failed to fetch categories');

            // Extract data
            const event = eventResponse.data.data;
            const tickets = ticketResponse.data.data;
            const registrations = registrationsResponse.data.data;
            const payments = paymentResponse.data.data;
            const categories = categoryResponse.data.data;
            const enrichedEvent = {
                ...event,
                ticketCount: tickets.length || 0,
                registrationCount: registrations.length || 0,
                paidPaymentCount: payments.filter((pay) => (pay.status === 'paid')).length || 0,
                paidPaymentTotalAmount: payments
                    .filter(pay => pay.status === 'paid' && !isNaN(pay.amount) && pay.amount !== null && pay.amount !== 'Free')
                    .reduce((total, pay) => total + Number(pay.paymentDetails?.payhere_amount ?? 0), 0),
                // manualPaymentCount: payments.filter((pay) => (pay.status === 'manual')).length || 0,
                manualPaymentCount: tickets.filter((ticket) => (ticket.bookedBy === 'admin')).length || 0,
                categoryCount: categories.length || 0,
                categories,
            };

            setEventsData(enrichedEvent);
            setTicketsData(tickets);
            setRegistrationsData(registrations);
            setTotalTicketsCount(tickets.length);
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsEventLoading(false);
        }
    };

    const fetchTickets = async () => {
        try {
            setIsLoading(true);
            
            // First, get all tickets without pagination to apply filtering correctly
            const [allTicketsResponse, registrationsResponse, paymentResponse, categoryResponse] = await Promise.all([
                axios.get(`${apiUrl}event-service/event-ticket`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/event-registration`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/event-payment`, { params: { event: id } }),
                axios.get(`${apiUrl}event-service/ticket-category`, { params: { event: id } }),
            ]);

            // Validate responses
            if (!allTicketsResponse.data?.success || !allTicketsResponse.data?.data) throw new Error('Failed to fetch tickets');
            if (!registrationsResponse.data?.success || !registrationsResponse.data?.data) throw new Error('Failed to fetch registrations');
            if (!paymentResponse.data?.success || !paymentResponse.data?.data) throw new Error('Failed to fetch payments');
            if (!categoryResponse.data?.success || !categoryResponse.data?.data) throw new Error('Failed to fetch categories');

            const allTickets = allTicketsResponse?.data.data;
            const registrations = registrationsResponse?.data.data;
            const payments = paymentResponse?.data.data;
            const categories = categoryResponse?.data.data;
            
            const enrichedTickets = allTickets.map(ticket => ({
                ...ticket,
                registrationDetails: registrations.find(reg => reg.id === ticket.registrationId) || {},
                paymentDetails: payments.find(pay => pay.registrationId === ticket.registrationId) || {},
                categoryDetails: categories.find(cat => cat.id === ticket.ticketCategoryId) || {},
            }));

            setTicketsTableData(enrichedTickets);
            
            // Apply filtering based on search term and payment status
            let filteredTickets = enrichedTickets;
            
            // Filter by search term first
            if (searchTerm) {
                filteredTickets = filteredTickets.filter((ticket) =>
                    ticket.referenceId && ticket.referenceId.toLowerCase().includes(searchTerm.toLowerCase())
                );
            }
            
            // Then filter by payment status
            if (paymentStatusFilter !== 'all') {
                filteredTickets = filteredTickets.filter((ticket) => {
                    // Handle free events
                    if (paymentStatusFilter === 'free') {
                        return ticket.event?.isFree === true;
                    }
                    
                    // Handle paid/pending status
                    const ticketPaymentStatus = ticket.paymentDetails?.status?.toLowerCase();
                    if (paymentStatusFilter === 'paid') {
                        return ticketPaymentStatus === 'paid';
                    } else if (paymentStatusFilter === 'pending') {
                        return ticketPaymentStatus === 'pending' || !ticketPaymentStatus;
                    }
                    
                    return false;
                });
            }
            
            // Calculate pagination for filtered results
            const totalFilteredCount = filteredTickets.length;
            const totalPages = Math.ceil(totalFilteredCount / rowsPerPage);
            const startIndex = (currentPage - 1) * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            const paginatedFilteredTickets = filteredTickets.slice(startIndex, endIndex);
            
            setFilteredTickets(paginatedFilteredTickets);
            setTotalItemsCount(totalFilteredCount);
            setTotalPagesCount(totalPages);
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
        finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchEvent();
    }, [id])


    const handleSearch = (term) => {
        const normalizedTerm = term ? term.startsWith("#") ? term : `#${term}` : '';
        setSearchTerm(normalizedTerm);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const handlePaymentStatusChange = (status) => {
        setPaymentStatusFilter(status);
        // Trigger a re-fetch with the new filter
        setCurrentPage(1); // Reset to first page when filter changes
        // The useEffect will handle the actual filtering
    };

    useEffect(() => {
        fetchTickets();
    }, [currentPage, rowsPerPage, searchTerm, paymentStatusFilter, bearerToken, id]);

    const handleClickOnRow = (id, eventId) => {
        navigate(`/view-eventTickets/detailedView/singleRegistration/${id}/${eventId}`);
    }

    const formattedDate = (date) => {
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(date).toLocaleDateString('en-GB', options);
    };

    const columns = [
        {
            key: 'referenceId', label: 'Reference ID',
            render: (row) => (row.referenceId || '--'),
        },
        { key: 'bookedDate', label: 'Booked Date', render: (row) => formattedDate(row.createdAt) },
        {
            key: 'bookedBy',
            label: 'Booked By',
            render: (row) => (row.registrationDetails?.name || '--')
        },
        {
            key: 'price',
            label: 'Price',
            render: (row) => (row.price || '--')
        },
        {
            key: 'email',
            label: 'Email',
            render: (row) => (row.registrationDetails?.email || '--')
        },
        {
            key: 'phone',
            label: 'Phone',
            render: (row) => (row.registrationDetails?.phone || '--')
        },
        {
            key: 'paymentStatus',
            label: 'Payment Status',
            render: (row) => (row.event?.isFree ? 'Free Event' : row.paymentDetails?.status
                ? row.paymentDetails.status.charAt(0).toUpperCase() + row.paymentDetails.status.slice(1)
                : '--'
            )
        },
        {
            key: 'categoryName',
            label: 'Ticket Type',
            render: (row) => (row.categoryDetails?.name || '--')
        },
    ];

    const headers = [
        { label: "Reference Id", key: "referenceId" },
        { label: "Event", key: "eventName" },
        { label: "Name", key: "name" },
        { label: "Email", key: "email" },
        { label: "Phone", key: "phone" },
        { label: "Category", key: "category" },
    ];

    const data = ticketsTableData.map((ticket) => ({
        referenceId: ticket.referenceId,
        eventName: eventsData?.name,
        name: ticket.registrationDetails?.name,
        email: ticket.registrationDetails?.email,
        phone: ticket.registrationDetails?.phone,
        category: ticket.categoryDetails?.name,
    }));

    return (
        <TailwindLayout>
            <TopBar />
            {isEventLoading ? (
                <div className="p-12 bg-gray-100 min-h-screen animate-pulse space-y-6">
                    {/* Event Title Skeleton */}
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <div className="h-6 bg-gray-300 rounded w-1/4 mb-6"></div>

                        {/* Stats Grid Skeleton */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-white rounded-lg shadow p-6 flex items-center">
                                    <div className="rounded-full bg-gray-300 h-10 w-10 mr-4" />
                                    <div className="space-y-2">
                                        <div className="h-5 bg-gray-300 rounded w-24" />
                                        <div className="h-4 bg-gray-200 rounded w-36" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t my-4"></div>

                        {/* Ticket Categories Skeleton */}
                        <div className="h-5 bg-gray-300 w-1/5 rounded mb-4" />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-white rounded-lg shadow p-6 space-y-2">
                                    <div className="h-6 bg-gray-300 rounded w-16" />
                                    <div className="h-4 bg-gray-200 rounded w-32" />
                                </div>
                            ))}
                        </div>

                        {/* CSV Button Skeleton */}
                        <div className="flex justify-end mb-4">
                            <div className="h-10 w-60 bg-gray-300 rounded" />
                        </div>

                        {/* Table Skeleton */}
                        <div className="space-y-2">
                            <div className="h-5 bg-gray-300 w-1/5 rounded" />
                            <div className="h-12 bg-gray-200 rounded" />
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-12 bg-gray-100 rounded" />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="p-12 bg-gray-100 min-h-screen mb-8">
                    <div className='bg-white rounded-lg shadow-lg p-6'>
                        <p className='pt-2 pb-4 text-xl font-semibold'>{eventsData.name}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                            <div className="bg-white rounded-lg shadow p-6 flex items-center">
                                <div className="rounded-full bg-black/5 p-3 mr-4">
                                    <Icon icon="streamline:tickets" className="h-8 w-8 p-1" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold">{eventsData?.ticketCount}</div>
                                    <div className="text-gray-500">Total Tickets Booked</div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6 flex items-center">
                                <div className="rounded-full bg-black/5 p-3 mr-4">
                                    <Icon icon="streamline:tickets" className="h-8 w-8 p-1" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold">{eventsData?.paidPaymentCount}</div>
                                    <div className="text-gray-500">Total Online Tickets Booked</div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6 flex items-center">
                                <div className="rounded-full bg-black/5 p-3 mr-4">
                                    <Icon icon="streamline:tickets" className="h-8 w-8 p-1" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold">{eventsData?.manualPaymentCount}</div>
                                    <div className="text-gray-500">Total Manual Tickets Booked</div>
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow p-6 flex items-center">
                                <div className="rounded-full bg-black/5 p-3 mr-4">
                                    <Icon icon="streamline:tickets" className="h-8 w-8 p-1" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold"><span className='text-sm mr-2'>LKR</span>{eventsData?.paidPaymentTotalAmount}</div>
                                    <div className="text-gray-500">Total Online Sales Amount</div>
                                </div>
                            </div>

                        </div>
                        <div className='border-t'></div>
                        {/* Ticket Category */}
                        <p className='pt-4 text-lg font-semibold'>Ticket Categories</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-4">
                            {eventsData.categories?.length > 0 ? (
                                eventsData.categories.map((category) => (
                                    <div key={category.id} className="bg-white rounded-lg shadow p-6">
                                        <div className="text-3xl font-bold">{category.soldTickets}</div>
                                        <div className="text-gray-500">{category.name}</div>
                                    </div>
                                ))
                            ) : (
                                <div className="bg-white rounded-lg shadow p-6">
                                    <div className="text-gray-500">Categories not found</div>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end mb-2">
                            <CSVLink data={data} headers={headers} filename={"Tickets"}
                                className="bg-gray-200 hover:bg-gray-300 text-black py-2 px-4 rounded">
                                Download Registrations As CSV
                            </CSVLink>
                        </div>

                        {/* tickets table */}
                        <DataTable
                            title={'Tickets'}
                            rows={filteredTickets}
                            columns={columns}
                            // onActionClick={handleActionClick}
                            onRowClick={handleClickOnRow}
                            options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: false, addoption: false, statusFilterOption: true, dateFilterOption: false, typeFilterOption: false, searchOption: true, actionColumn: false }}
                            totalCount={totalItemsCount}
                            pagesCount={totalPagesCount}
                            currentPage={currentPage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                            onPageChange={(page) => setCurrentPage(page)}
                            // eventStatus={eventStatus}
                            // onStatusChange={(status) => setEventStatus(status)}
                            // categories={eventCategories}
                            // onCategoryChange={(id) => setSelectedCategoryId(id)}
                            // category={selectedCategoryName}
                            // onDateChange={(date) => setSelectedDate(date)}
                            // filteredDate={selectedDate}
                            onSearch={handleSearch}
                            searcItem={searchTerm}
                            onStatusChange={handlePaymentStatusChange}
                            eventStatus={paymentStatusFilter}
                            // role={userRole}
                            loaderStatus={isLoading}
                        />

                    </div>
                </div>
            )}
            <FooterTailwind />
        </TailwindLayout>
    )
}
